/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { usePermissions } from 'src/modules/shared/contexts/permissions-context';
import { AdminTenantPermission } from 'src/modules/shared/types/permissions.type';
import { SUPPORT_TENANT_STORAGE_KEY } from 'src/modules/shared/constants/http-headers.constants';

interface SupportTenantContextValue {
  assumedTenantId: string | null;
  isSupportMode: boolean;
  enableSupportMode: (tenantId: string) => void;
  disableSupportMode: () => void;
  canUseSupportMode: boolean;
  loading: boolean;
}

const SupportTenantContext = createContext<SupportTenantContextValue | null>(
  null
);

const readStoredTenantId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const storedValue = window.localStorage.getItem(SUPPORT_TENANT_STORAGE_KEY);
  return storedValue && storedValue.trim().length > 0
    ? storedValue.trim()
    : null;
};

interface SupportTenantProviderProps {
  children: ReactNode;
}

export const SupportTenantProvider = ({
  children,
}: SupportTenantProviderProps) => {
  const { hasPermission, loading } = usePermissions();

  const [assumedTenantId, setAssumedTenantId] = useState<string | null>(() =>
    readStoredTenantId()
  );

  const canUseSupportMode = useMemo(() => {
    if (loading) return false;

    return hasPermission(AdminTenantPermission.Assume);
  }, [hasPermission, loading]);

  const persistAssumedTenant = useCallback((tenantId: string | null) => {
    if (typeof window === 'undefined') return;

    if (tenantId && tenantId.trim().length > 0) {
      window.localStorage.setItem(SUPPORT_TENANT_STORAGE_KEY, tenantId.trim());
    } else {
      window.localStorage.removeItem(SUPPORT_TENANT_STORAGE_KEY);
    }
  }, []);

  const enableSupportMode = useCallback(
    (tenantId: string) => {
      if (!canUseSupportMode) {
        return;
      }

      const normalizedTenantId = tenantId.trim();
      if (!normalizedTenantId) {
        return;
      }

      setAssumedTenantId(normalizedTenantId);
      persistAssumedTenant(normalizedTenantId);
    },
    [canUseSupportMode, persistAssumedTenant]
  );

  const disableSupportMode = useCallback(() => {
    setAssumedTenantId(null);
    persistAssumedTenant(null);
  }, [persistAssumedTenant]);

  useEffect(() => {
    if (loading) return;

    if (!canUseSupportMode && assumedTenantId) {
      disableSupportMode();
    }
  }, [assumedTenantId, canUseSupportMode, disableSupportMode, loading]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SUPPORT_TENANT_STORAGE_KEY) return;

      if (event.newValue && event.newValue.trim().length > 0) {
        setAssumedTenantId(event.newValue.trim());
      } else {
        setAssumedTenantId(null);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const value: SupportTenantContextValue = {
    assumedTenantId,
    isSupportMode: !!assumedTenantId,
    enableSupportMode,
    disableSupportMode,
    canUseSupportMode,
    loading,
  };

  return (
    <SupportTenantContext.Provider value={value}>
      {children}
    </SupportTenantContext.Provider>
  );
};

export const useSupportTenant = (): SupportTenantContextValue => {
  const context = useContext(SupportTenantContext);
  if (!context) {
    throw new Error(
      'useSupportTenant must be used within a SupportTenantProvider'
    );
  }
  return context;
};
